import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ProjectsCard } from '../../../../shared/components/projects-card/projects-card';
import { Project } from '../../../../core/models/project.model';
import { ApiService } from '../../../../core/services/api.service';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { LanguageService } from '../../../../core/services/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-home-projects',
  templateUrl: './home-projects.html',
  styleUrl: './home-projects.scss',
  imports: [CommonModule, ProjectsCard, TranslatePipe,RouterLink],
  standalone: true
})
export class HomeProjects implements OnInit, OnDestroy {
  @ViewChild('scrollTrack') scrollTrack!: ElementRef<HTMLElement>;

  projects: Project[] = [];
  isLoading = false;
  errorMessage = '';
  private subscription: Subscription | null = null;
  private autoScrollInterval: any;

  readonly SCROLL_AMOUNT = 420;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.stopAutoScroll();
  }

  get isRtl(): boolean {
    return this.languageService.getCurrentLanguage() === 'ar';
  }

  scrollPrev(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    el.scrollBy({
      left: this.isRtl ? this.SCROLL_AMOUNT : -this.SCROLL_AMOUNT,
      behavior: 'smooth',
    });
    this.startAutoScroll();
  }

  scrollNext(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    el.scrollBy({
      left: this.isRtl ? -this.SCROLL_AMOUNT : this.SCROLL_AMOUNT,
      behavior: 'smooth',
    });
    this.startAutoScroll();
  }

  startAutoScroll(): void {
    this.stopAutoScroll();
    if (typeof window !== 'undefined') {
      this.autoScrollInterval = window.setInterval(() => {
        this.autoScrollNext();
      }, 1500);
    }
  }

  stopAutoScroll(): void {
    if (typeof window !== 'undefined' && this.autoScrollInterval) {
      window.clearInterval(this.autoScrollInterval);
    }
  }

  autoScrollNext(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;
    
    const currentScroll = Math.abs(el.scrollLeft);
    if (currentScroll >= maxScroll - 10) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollBy({
        left: this.isRtl ? -this.SCROLL_AMOUNT : this.SCROLL_AMOUNT,
        behavior: 'smooth',
      });
    }
  }

  loadProjects(): void {
    this.isLoading = true;
    this.errorMessage = '';
    const endpoint = '/public/search/projects-and-profiles?targetType=project';
    
    this.subscription = this.apiService.get<any>(endpoint).subscribe({
      next: (response) => {
        const data = response?.results?.data ?? [];
        
        this.projects = data.map((p: any) => ({
          id: p.targetId || p._id || '',
          targetId: p.targetId || p._id || '',
          name: p.project?.name || p.name || '',
          developer: p.developer?.name || p.owner?.name || '',
          location: p.project?.location || p.location || '',
          imageUrl: p.thumbnail || p.imageUrl || '',
          thumbnail: p.thumbnail || '',
          deliveryDate: p.project?.deliveryDate || p.deliveryDate || '',
          unitTypes: p.project?.type ? [p.project.type] : (Array.isArray(p.unitTypes) ? p.unitTypes : []),
          startingPrice: p.project?.priceStart || p.startingPrice || 0,
          totalUnits: p.project?.plannedUnits || p.totalUnits || 0,
          currency: p.currency || 'EGP',
          isFavorite: p.isFavorite || false,
          isFeatured: p.isFeatured || false,
          isTrusted: p.isTrusted || false,
          rating: p.rating || 0,
          totalReviews: p.totalReviews || 0,
          project: p.project,
        }));

        this.isLoading = false;
        setTimeout(() => this.startAutoScroll(), 100);
      },
      error: () => {
        this.errorMessage = 'حدث خطأ أثناء تحميل المشاريع';
        this.isLoading = false;
      },
    });
  }

  onPropertyClick(project: Project): void {
    if (project.id) {
      this.router.navigate(['/projects', project.id], { state: { project } });
    }
  }

  viewAll(): void {
    this.router.navigate(['/all-properties']); // Assuming projects are listed here
  }
}
